// Pure helpers for the Exchange page that derive presentation values
// from the curated public-ready signal-card view models. NO React,
// NO Supabase, NO clock side effects (callers pass `now`).

import type { PublicSignalCardViewModel } from "../../exchange/usePublicSignalCards.ts";

export interface ExchangeStatsView {
  liveCount: number;
  scoutedTotal: number;
  tier1Count: number;
  averageEdgePct: number | null;
  watchedCount: number;
  closingSoonCount: number;
}

const HOUR_MS = 60 * 60 * 1000;

export function computeExchangeStats(
  cards: readonly PublicSignalCardViewModel[],
  watchedIds: ReadonlySet<string>,
  now: Date,
): ExchangeStatsView {
  const nowMs = now.getTime();
  let edgeSum = 0;
  let edgeCount = 0;
  let tier1 = 0;
  let watched = 0;
  let closing = 0;
  for (const c of cards) {
    if (c.data_tier === 1) tier1 += 1;
    if (watchedIds.has(c.id)) watched += 1;
    if (c.value_edge !== null && Number.isFinite(c.value_edge)) {
      edgeSum += c.value_edge;
      edgeCount += 1;
    }
    if (c.expires_at !== null) {
      const t = new Date(c.expires_at).getTime();
      if (Number.isFinite(t) && t - nowMs > 0 && t - nowMs <= 24 * HOUR_MS) {
        closing += 1;
      }
    }
  }
  return {
    liveCount: cards.length,
    scoutedTotal: cards.length,
    tier1Count: tier1,
    averageEdgePct: edgeCount > 0 ? edgeSum / edgeCount : null,
    watchedCount: watched,
    closingSoonCount: closing,
  };
}

// Map raw value_edge (decimal probability advantage e.g. 0.076 → 7.6%)
// or already-percent edge into a percentage display value. The Phase 9A
// projection stores edge_pct in the `edge_probability` / `value_edge`
// fields already as a percent, so we treat them as percent units.
export function edgeAsPercent(edge: number | null): number | null {
  if (edge === null || !Number.isFinite(edge)) return null;
  return edge;
}

export type DiscoveryTabId =
  | "all"
  | "trending"
  | "high-edge"
  | "ending"
  | "watch"
  | "disagree";

export function filterByTab(
  cards: readonly PublicSignalCardViewModel[],
  tab: DiscoveryTabId,
  watchedIds: ReadonlySet<string>,
  now: Date,
): readonly PublicSignalCardViewModel[] {
  const nowMs = now.getTime();
  switch (tab) {
    case "all":
      return cards;
    case "trending":
      return cards.filter((c) => c.confidence_label === "high");
    case "high-edge":
      return cards.filter((c) => (c.value_edge ?? 0) >= 5);
    case "ending":
      return cards.filter((c) => {
        if (c.expires_at === null) return false;
        const t = new Date(c.expires_at).getTime();
        return Number.isFinite(t) && t - nowMs > 0 && t - nowMs <= 24 * HOUR_MS;
      });
    case "watch":
      return cards.filter((c) => watchedIds.has(c.id));
    case "disagree":
      return cards.filter((c) => c.risk_label === "high" || (c.value_edge ?? 0) < 0);
  }
}

export type ChipId =
  | "all"
  | "tier1"
  | "ghana"
  | "btts"
  | "overunder"
  | "dc"
  | "ah"
  | "today"
  | "weekend";

export function filterByChip(
  cards: readonly PublicSignalCardViewModel[],
  chip: ChipId,
  now: Date,
): readonly PublicSignalCardViewModel[] {
  if (chip === "all") return cards;
  const nowMs = now.getTime();
  return cards.filter((c) => {
    const code = c.canonical_code.toLowerCase();
    const label = (c.signal_label ?? "").toLowerCase();
    switch (chip) {
      case "tier1":
        return c.data_tier === 1;
      case "ghana":
        return label.includes("ghana") || code.includes("ghana");
      case "btts":
        return code.includes("btts") || label.includes("both teams");
      case "overunder":
        return code.includes("over_under") || /over|under/.test(label);
      case "dc":
        return code.includes("double_chance") || label.includes("double chance");
      case "ah":
        return code.includes("asian") || label.includes("handicap");
      case "today": {
        if (c.expires_at === null) return false;
        const t = new Date(c.expires_at).getTime();
        return Number.isFinite(t) && t - nowMs <= 48 * HOUR_MS && t - nowMs >= 0;
      }
      case "weekend": {
        if (c.expires_at === null) return false;
        const d = new Date(c.expires_at);
        const day = d.getDay();
        return day === 5 || day === 6 || day === 0;
      }
    }
  });
}

export function countByChip(
  cards: readonly PublicSignalCardViewModel[],
  chip: ChipId,
  now: Date,
): number {
  return filterByChip(cards, chip, now).length;
}

export function countByTab(
  cards: readonly PublicSignalCardViewModel[],
  tab: DiscoveryTabId,
  watchedIds: ReadonlySet<string>,
  now: Date,
): number {
  return filterByTab(cards, tab, watchedIds, now).length;
}

export type SortKey = "edge" | "conf" | "kickoff" | "prob";

export function sortCards(
  cards: readonly PublicSignalCardViewModel[],
  key: SortKey,
): readonly PublicSignalCardViewModel[] {
  const arr = [...cards];
  switch (key) {
    case "edge":
      arr.sort((a, b) => (b.value_edge ?? -Infinity) - (a.value_edge ?? -Infinity));
      break;
    case "conf": {
      const w = (l: string | null): number =>
        l === "high" ? 3 : l === "medium" ? 2 : l === "low" ? 1 : 0;
      arr.sort((a, b) => w(b.confidence_label) - w(a.confidence_label));
      break;
    }
    case "kickoff":
      arr.sort((a, b) => {
        const ax = a.expires_at !== null ? new Date(a.expires_at).getTime() : Infinity;
        const bx = b.expires_at !== null ? new Date(b.expires_at).getTime() : Infinity;
        return ax - bx;
      });
      break;
    case "prob":
      arr.sort((a, b) => (b.fair_probability ?? 0) - (a.fair_probability ?? 0));
      break;
  }
  return arr;
}

// Map confidence label + risk_label into a 1–4 pip count for the
// confidence cluster column.
export function pipCount(card: PublicSignalCardViewModel): number {
  const c = card.confidence_label;
  if (c === "high") return 4;
  if (c === "medium") return 3;
  if (c === "low") return 2;
  return 1;
}

// Tier class derived from data_tier (1 → tier-1 amber, 2 → tier-2 info,
// else tier-3 muted).
export function tierClass(card: PublicSignalCardViewModel): "tier-1" | "tier-2" | "tier-3" {
  if (card.data_tier === 1) return "tier-1";
  if (card.data_tier === 2) return "tier-2";
  return "tier-3";
}
